import { X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import {
  useBulkRetryVorgaenge,
  useVorgang,
} from '@/adapter/opm-cockpit-backend/useVorgangApi';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { VorgangProzessSchritt } from '@/features/schemaValidation/stepValidationSchemas';
import { cn } from '@/lib/utils';
import { PayloadTree } from './PayloadTree';
import { useMassSavePfadWerte } from '../hooks/useMassSavePfadWerte';
import {
  findFehlerPayload,
  getValueAtPath,
  inferPathType,
  isDescendantPath,
  parseUserValue,
  setValueAtPath,
  type PathType,
} from '../types';

const ORIGINAL_VALUE_PREVIEW_MAX_LENGTH = 60;

const LABEL_RETRY = 'Neu verarbeiten';
const LABEL_SAVE_AND_RETRY = 'Speichern & neu verarbeiten';
const LABEL_SAVE_ONLY = 'Speichern';
const LABEL_SAVING = 'Speichere…';
const LABEL_RETRYING = 'Verarbeite…';

const BOOLEAN_VALUE_OPTIONS = [
  { value: 'true', label: 'true' },
  { value: 'false', label: 'false' },
];

type PendingAction = 'save-and-retry' | 'retry' | 'save-only' | null;

function previewOriginal(value: unknown): string {
  if (value === null || value === undefined) return '—';
  const str = typeof value === 'string' ? value : JSON.stringify(value);
  return str.length > ORIGINAL_VALUE_PREVIEW_MAX_LENGTH
    ? `${str.slice(0, ORIGINAL_VALUE_PREVIEW_MAX_LENGTH)}…`
    : str;
}

interface PfadWertBearbeitungProps {
  vorgangIds: string[];
  schritt: VorgangProzessSchritt;
  selectedPaths: ReadonlySet<string>;
  patchValues: Readonly<Record<string, string>>;
  pathTypeOverrides: Readonly<Record<string, PathType>>;
  pathsToDelete: ReadonlySet<string>;
  onTogglePath: (path: string) => void;
  onSetValue: (path: string, value: string) => void;
  onAddPath: (path: string, type: PathType) => void;
  onToggleDelete: (path: string) => void;
  onSaved?: (
    savedVorgangIds: string[],
    options?: { keepSelection?: boolean },
  ) => void;
}

export const PfadWertBearbeitung: React.FC<PfadWertBearbeitungProps> = ({
  vorgangIds,
  schritt,
  selectedPaths,
  patchValues,
  pathTypeOverrides,
  pathsToDelete,
  onTogglePath,
  onSetValue,
  onAddPath,
  onToggleDelete,
  onSaved,
}) => {
  const ersterId = vorgangIds[0] ?? '';
  const { data, isLoading, error } = useVorgang(ersterId);
  const { save, isSaving } = useMassSavePfadWerte(schritt);
  const bulkRetry = useBulkRetryVorgaenge();
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  const fehlerPayload = useMemo(
    () => findFehlerPayload(data, schritt),
    [data, schritt],
  );

  // Pfade unter einem zum Löschen markierten Vorfahren sind blockiert —
  // weder anzeigen noch speichern, da der Container ohnehin gelöscht wird.
  const hasDeletedAncestor = (path: string): boolean => {
    for (const deleted of pathsToDelete) {
      if (isDescendantPath(path, deleted)) return true;
    }
    return false;
  };

  const selectedPathsList = Array.from(selectedPaths).filter(
    (path) => !hasDeletedAncestor(path),
  );
  const pathsToDeleteList = Array.from(pathsToDelete).filter(
    (path) => !hasDeletedAncestor(path),
  );

  const resolvePathType = (path: string): PathType =>
    pathTypeOverrides[path] ??
    inferPathType(getValueAtPath(fehlerPayload, path));

  // Baut den fehlerPayload um neu angelegte Container an, damit sie im Baum
  // sichtbar sind und der User dort wieder „+" klicken kann.
  const virtualPayload = useMemo(() => {
    let result: unknown = fehlerPayload;
    for (const path of selectedPaths) {
      if (pathTypeOverrides[path] !== 'object') continue;
      if (getValueAtPath(result, path) !== undefined) continue;
      result = setValueAtPath(result, path, {});
    }
    return result;
  }, [fehlerPayload, selectedPaths, pathTypeOverrides]);

  // Container-Patches, deren Kinder ebenfalls gespeichert werden, sind
  // redundant — setValueAtPath legt fehlende Zwischen-Container automatisch an.
  // Ohne Filter würde ein Container-Patch die Kinder wieder leeren.
  const buildPatches = () =>
    selectedPathsList
      .filter((path) => {
        if (resolvePathType(path) !== 'object') return true;
        const prefix = `${path}.`;
        return !selectedPathsList.some(
          (p) => p !== path && p.startsWith(prefix),
        );
      })
      .map((path) => ({
        path,
        value: parseUserValue(patchValues[path] ?? '', resolvePathType(path)),
      }));

  const handleNeuVerarbeiten = async () => {
    setPendingAction(hasChanges ? 'save-and-retry' : 'retry');
    try {
      const { success: vorgangIdsToRetry } = hasChanges
        ? await save({
            vorgangIds,
            patches: buildPatches(),
            deletes: pathsToDeleteList,
            suppressSuccessToast: true,
          })
        : {
            // Bei (nur) "Neu verarbeiten" einfach die selektierten Vorgänge neu verarbeiten.
            success: vorgangIds,
          };
      if (vorgangIdsToRetry.length > 0) {
        await bulkRetry.mutateAsync({ ids: vorgangIds });
      }
      onSaved?.(vorgangIdsToRetry);
    } finally {
      setPendingAction(null);
    }
  };

  const handleNurSpeichern = async () => {
    setPendingAction('save-only');
    try {
      const result = await save({
        vorgangIds,
        patches: buildPatches(),
        deletes: pathsToDeleteList,
        suppressSuccessToast: false,
      });
      if (!result) return;
      onSaved?.(result.success, { keepSelection: true });
    } finally {
      setPendingAction(null);
    }
  };

  const hasChanges =
    selectedPathsList.length > 0 || pathsToDeleteList.length > 0;
  const sharedDisabled = isSaving || bulkRetry.isPending;

  const retryLabel =
    pendingAction === 'retry'
      ? LABEL_RETRYING
      : pendingAction === 'save-and-retry'
        ? isSaving
          ? LABEL_SAVING
          : LABEL_RETRYING
        : hasChanges
          ? LABEL_SAVE_AND_RETRY
          : LABEL_RETRY;

  const saveOnlyLabel =
    pendingAction === 'save-only' ? LABEL_SAVING : LABEL_SAVE_ONLY;

  return (
    <section className="space-y-5 pt-5 pb-10">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold">
            Pfade & Werte ({vorgangIds.length}
            {vorgangIds.length === 1 ? ' Vorgang' : ' Vorgänge'})
          </h2>

          <p className="text-sm text-text-secondary">
            Pfade aus Vorgang #{ersterId} — Format-gleich heißt nicht zwingend
            feldgleich.
            <br />
            Im JSON werden nur die Werte des zuerst ausgewählten Vorgangs
            angezeigt.
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={handleNurSpeichern}
            disabled={sharedDisabled || !hasChanges}
          >
            {saveOnlyLabel}
          </Button>
          <Button
            type="button"
            onClick={handleNeuVerarbeiten}
            disabled={sharedDisabled}
          >
            {retryLabel}
          </Button>
        </div>
      </div>

      {error ? (
        <div className="text-red-500">
          Fehler beim Laden des Vorgangs-Details.
        </div>
      ) : null}

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-[3fr_2fr]">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-[3fr_2fr]">
          <div className="rounded-md border bg-background p-3">
            <h3 className="mb-2 text-sm font-medium text-text-primary">
              Payload des ersten Vorgangs
            </h3>
            {fehlerPayload === null || fehlerPayload === undefined ? (
              <p className="text-sm text-text-secondary">
                Kein Payload für den Fehler-Schritt verfügbar.
              </p>
            ) : (
              <PayloadTree
                value={virtualPayload}
                selectedPaths={selectedPaths}
                pathsToDelete={pathsToDelete}
                onTogglePath={onTogglePath}
                onAddPath={onAddPath}
                onToggleDelete={onToggleDelete}
              />
            )}
          </div>

          <div className="space-y-3">
            <div className="rounded-md border bg-background p-3">
              <h3 className="mb-2 text-sm font-medium text-text-primary">
                Geänderte Pfade ({selectedPathsList.length})
              </h3>
              {selectedPathsList.length === 0 ? (
                <p className="text-sm text-text-secondary">
                  Wähle links einen oder mehrere Pfade, um sie zu bearbeiten —
                  oder klicke „Feld hinzufügen", um einen neuen Pfad anzulegen.
                </p>
              ) : (
                <ul className="space-y-3">
                  {selectedPathsList.map((path) => {
                    const original = getValueAtPath(fehlerPayload, path);
                    const aktuell = patchValues[path] ?? '';
                    const pathType = resolvePathType(path);

                    let editor: React.ReactNode;
                    if (pathType === 'object') {
                      editor = (
                        <div className="text-xs italic text-text-secondary">
                          Leeres Objekt — Felder im Baum mit „+" anlegen.
                        </div>
                      );
                    } else if (pathType === 'boolean') {
                      editor = (
                        <Select
                          value={aktuell}
                          onChange={(event) =>
                            onSetValue(path, event.target.value)
                          }
                          options={BOOLEAN_VALUE_OPTIONS}
                          placeholder="— wählen —"
                          className="h-8 py-1"
                        />
                      );
                    } else {
                      editor = (
                        <input
                          type="text"
                          value={aktuell}
                          onChange={(event) =>
                            onSetValue(path, event.target.value)
                          }
                          placeholder="Neuer Wert"
                          className={cn(
                            'flex h-8 w-full rounded-md border border-gray-300 bg-white px-2 py-1 font-mono text-sm',
                            'focus:border-blue-500 focus:ring-2 focus:ring-blue-500',
                          )}
                        />
                      );
                    }

                    return (
                      <li key={path} className="space-y-1 rounded border p-2">
                        <div className="flex items-start justify-between gap-2">
                          <code className="break-all font-mono text-xs text-text-primary">
                            {path}
                          </code>
                          <div className="flex items-center gap-2">
                            <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] uppercase text-text-secondary">
                              {pathType}
                            </span>
                            <button
                              type="button"
                              onClick={() => onTogglePath(path)}
                              className="text-text-secondary hover:text-text-primary"
                              aria-label={`Pfad ${path} entfernen`}
                            >
                              <X className="size-4" />
                            </button>
                          </div>
                        </div>
                        <div className="text-xs text-text-secondary">
                          Aktuell:{' '}
                          <span className="font-mono">
                            {previewOriginal(original)}
                          </span>
                        </div>
                        {editor}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {pathsToDeleteList.length > 0 ? (
              <div className="rounded-md border bg-background p-3">
                <h3 className="mb-2 text-sm font-medium text-text-primary">
                  Zu löschen ({pathsToDeleteList.length})
                </h3>
                <ul className="space-y-2">
                  {pathsToDeleteList.map((path) => (
                    <li
                      key={path}
                      className="flex items-start justify-between gap-2 rounded border p-2"
                    >
                      <code className="break-all font-mono text-xs text-red-700 line-through">
                        {path}
                      </code>
                      <button
                        type="button"
                        onClick={() => onToggleDelete(path)}
                        className="text-text-secondary hover:text-text-primary"
                        aria-label={`Löschen von ${path} rückgängig`}
                      >
                        <X className="size-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
};
